// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {ssrSafeDocument} from '@github-ui/ssr-utils'

/**
 * Deferred loader for required bundles behind the `defer_required_bundles_after_react` feature flag.
 *
 * When the flag is enabled the server renders github-elements, element-registry,
 * behaviors, and notifications-global as `<link rel="modulepreload" data-deferred-bundle>`
 * instead of `<script>` tags. This initializer dynamically imports them at the right time:
 *
 *   1. On React pages - after the first React root paints (detected via a
 *      MutationObserver watching for the `loaded` class on `react-app` elements).
 *   2. On non-React pages - on DOMContentLoaded.
 */
export function startLazyRequiredLoader(): () => void {
  if (!ssrSafeDocument) return () => {}
  const doc = ssrSafeDocument
  const reactApps = doc.querySelectorAll<HTMLElement>('react-app[app-name]')

  let loaded = false
  let deferredObserver: MutationObserver | null = null

  const reportDeferredBundleLoadError = (href: string, error: unknown): void => {
    reportError(error, {message: `[lazy-required-loader] Failed to load deferred bundle: ${href}`})
  }

  const teardown = (): void => {
    deferredObserver?.disconnect()
    doc.removeEventListener('DOMContentLoaded', onDOMContentLoaded)
  }

  const loadDeferred = (): void => {
    if (loaded) return
    loaded = true

    teardown()

    const links = doc.querySelectorAll<HTMLLinkElement>('link[data-deferred-bundle]')
    for (const link of links) {
      if (link.href) {
        void (async () => {
          try {
            await import(/* webpackIgnore: true */ link.href)
          } catch (error) {
            reportDeferredBundleLoadError(link.href, error)
          }
        })()
      }
    }
  }

  const onDOMContentLoaded = (): void => {
    if (reactApps.length === 0) {
      loadDeferred()
    }
  }

  // Wait for React paint on React pages by observing the `loaded` class
  // added by ReactBaseElement after rAF + rIC following commit.
  if (reactApps.length > 0 && typeof MutationObserver !== 'undefined') {
    deferredObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && (mutation.target as HTMLElement).classList.contains('loaded')) {
          loadDeferred()
          return
        }
      }
    })
    for (const app of reactApps) {
      // If the app is already loaded (e.g. deferred script ran late), trigger immediately.
      if (app.classList.contains('loaded')) {
        loadDeferred()
        break
      }
      deferredObserver.observe(app, {attributes: true, attributeFilter: ['class']})
    }
  }

  // Fallback: fire on DOMContentLoaded only when no React root is present.
  // Also check readyState in case DOMContentLoaded already fired before we ran.
  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', onDOMContentLoaded, {once: true})
  } else {
    onDOMContentLoaded()
  }

  return () => {
    teardown()
  }
}
